import { getInternalMetas } from "../internal/metas.js";
import { captureMetas, isMetaCaptured, markMetaCaptured } from "../metas/capture.js";
import _objectSpread2 from "../_virtual/_@oxc-project_runtime@0.150.0/helpers/esm/objectSpread2.js";
import { BatchExecutor } from "./batchExecutor.js";
import "./const.js";
//#region src/transports/initialize.ts
function initializeTransports(unpatchedConsole, internalLogger, config, metas) {
	var _config$batching3;
	internalLogger.debug("Initializing transports");
	const transports = [];
	let beforeSendHooks = [];
	const add = (...newTransports) => {
		internalLogger.debug("Adding transports");
		newTransports.forEach((newTransport) => {
			internalLogger.debug(`Adding "${newTransport.name}" transport`);
			if (transports.some((existingTransport) => existingTransport === newTransport)) {
				internalLogger.warn(`Transport ${newTransport.name} is already added`);
				return;
			}
			newTransport.unpatchedConsole = unpatchedConsole;
			newTransport.internalLogger = internalLogger;
			newTransport.config = config;
			newTransport.metas = metas;
			transports.push(newTransport);
		});
	};
	const addBeforeSendHooks = (...newBeforeSendHooks) => {
		internalLogger.debug("Adding beforeSendHooks\n", beforeSendHooks);
		newBeforeSendHooks.forEach((beforeSendHook) => {
			if (beforeSendHook) beforeSendHooks.push(beforeSendHook);
		});
	};
	const applyBeforeSendHooks = (items) => {
		let filteredItems = items;
		for (const hook of beforeSendHooks) {
			const modified = filteredItems.map(hook).filter(Boolean);
			if (modified.length === 0) return [];
			filteredItems = sanitizeItems(modified, config);
		}
		return filteredItems;
	};
	const batchedSend = (items) => {
		const filteredItems = applyBeforeSendHooks(items);
		if (filteredItems.length === 0) return;
		for (const transport of transports) {
			internalLogger.debug(`Transporting item using ${transport.name}\n`, filteredItems);
			if (transport.isBatched()) transport.send(filteredItems);
		}
	};
	const instantSend = (item) => {
		var _config$batching;
		if (((_config$batching = config.batching) === null || _config$batching === void 0 ? void 0 : _config$batching.enabled) && transports.every((transport) => transport.isBatched())) return;
		const [filteredItem] = applyBeforeSendHooks([item]);
		if (filteredItem === void 0) return;
		for (const transport of transports) {
			var _config$batching2;
			internalLogger.debug(`Transporting item using ${transport.name}\n`, filteredItem);
			if (!transport.isBatched()) transport.send(filteredItem);
			else if (!((_config$batching2 = config.batching) === null || _config$batching2 === void 0 ? void 0 : _config$batching2.enabled)) transport.send([filteredItem]);
		}
	};
	let batchExecutor;
	if ((_config$batching3 = config.batching) === null || _config$batching3 === void 0 ? void 0 : _config$batching3.enabled) batchExecutor = new BatchExecutor(batchedSend, {
		sendTimeout: config.batching.sendTimeout,
		itemLimit: config.batching.itemLimit,
		paused: config.paused
	});
	const execute = (item) => {
		var _config$batching4;
		if (config.paused || !getInternalMetas(metas).shouldCapture()) return;
		if (!isMetaCaptured(item.meta)) {
			var _metas$value$session, _itemMeta$session, _meta$session;
			const previousSessionId = (_metas$value$session = metas.value.session) === null || _metas$value$session === void 0 ? void 0 : _metas$value$session.id;
			const meta = captureMetas(metas);
			if (config.paused) return;
			const itemMeta = _objectSpread2({}, item.meta);
			if (((_itemMeta$session = itemMeta.session) === null || _itemMeta$session === void 0 ? void 0 : _itemMeta$session.id) === previousSessionId && ((_meta$session = meta.session) === null || _meta$session === void 0 ? void 0 : _meta$session.id) !== previousSessionId) {
				if (meta.session === void 0) delete itemMeta.session;
				else itemMeta.session = meta.session;
			}
			item = _objectSpread2(_objectSpread2({}, item), {}, { meta: markMetaCaptured(itemMeta) });
		}
		if ((_config$batching4 = config.batching) === null || _config$batching4 === void 0 ? void 0 : _config$batching4.enabled) batchExecutor === null || batchExecutor === void 0 || batchExecutor.addItem(item);
		instantSend(item);
	};
	const getBeforeSendHooks = () => [...beforeSendHooks];
	const isPaused = () => config.paused;
	const pause = () => {
		internalLogger.debug("Pausing transports");
		batchExecutor === null || batchExecutor === void 0 || batchExecutor.pause();
		config.paused = true;
	};
	const remove = (...transportsToRemove) => {
		internalLogger.debug("Removing transports");
		transportsToRemove.forEach((transportToRemove) => {
			internalLogger.debug(`Removing "${transportToRemove.name}" transport`);
			const existingTransportIndex = transports.indexOf(transportToRemove);
			if (existingTransportIndex === -1) {
				internalLogger.warn(`Transport "${transportToRemove.name}" is not added`);
				return;
			}
			transports.splice(existingTransportIndex, 1);
		});
	};
	const removeBeforeSendHooks = (...beforeSendHooksToRemove) => {
		internalLogger.debug("Removing beforeSendHooks");
		beforeSendHooks = beforeSendHooks.filter((beforeSendHook) => !beforeSendHooksToRemove.includes(beforeSendHook));
	};
	const unpause = () => {
		internalLogger.debug("Unpausing transports");
		batchExecutor === null || batchExecutor === void 0 || batchExecutor.start();
		config.paused = false;
	};
	return {
		add,
		addBeforeSendHooks,
		getBeforeSendHooks,
		execute,
		isPaused,
		pause,
		remove,
		removeBeforeSendHooks,
		get transports() {
			return [...transports];
		},
		unpause
	};
}
/**
* Removes the `payload.originalError` property from the provided `TransportItem[]` parameter.
*/
function sanitizeItems(filteredItems, config) {
	if (config.preserveOriginalError) {
		for (const item of filteredItems) if (item.type === "exception") delete item.payload.originalError;
	}
	return filteredItems;
}
//#endregion
export { initializeTransports };

//# sourceMappingURL=initialize.js.map